// The person's email signature in the compose window (onpoint repo:
// docs/EMAIL_SIGNATURES.md). Signatures are managed in OnPoint; the bot
// asks for the current one (GET /api/mail/signature) when a compose opens
// and shows it in the body — under what they type, above any quoted
// thread — exactly as Outlook does.
//
// On send the window tells the server what happened to it (signatureFlag):
// still there, deleted on purpose, a draft sent as it stands, or never shown.
// The server adds one only when it was never shown
// (bot/src/onpoint/signatures.js) — so a deleted signature stays deleted and
// nothing is ever sent with two.
//
// Promotions are for people outside the company: an email only colleagues
// receive shows the signature without the banner. The server decides (it
// asks OnPoint); the window tells it who the email is for when it opens and
// whenever the recipients change, and takes the banner out of its signature
// or puts it back (matchPromotion). The banner is an image, so neither
// counts as the person editing their signature.
//
// Which opens get one: every fresh compose — new, reply, reply all, forward.
// Not a draft reopened from Outlook's Drafts folder (it already holds
// whatever signature it was written with), and not a tab coming back from
// the strip (that remembers what happened the first time —
// signatureMemory).

import { sanitizedFragment } from './safeHtml';

export const SIGNATURE_ATTR = 'data-pp-signature';
/** The promotion banner inside a signature. */
export const PROMOTION_ATTR = 'data-pp-promotion';

/** compose kind for the API: reply | forward | new. */
export function composeKind(defaults) {
  if (defaults && defaults.replyToId) return 'reply';
  if (defaults && defaults.forwardId) return 'forward';
  return 'new';
}

/** Should this open fetch and show the signature? (see the top) */
export function wantsSignature(defaults) {
  const d = defaults || {};
  return !d.signatureHandled && d.mode !== 'draft';
}

/** An Outlook draft, or a tab that came from one: sent as it stands. */
export function isResumedDraft(defaults) {
  const d = defaults || {};
  return d.mode === 'draft' || !!d.signatureResumed;
}

/**
 * What a saved tab (or a failed send's retry draft) remembers, so reopening
 * it neither adds a second signature nor brings back a deleted one — and,
 * with the text it was inserted with, can still tell when it was edited.
 */
export function signatureMemory(inserted, resumed, text = '') {
  return { signatureHandled: true, signatureInserted: !!inserted, signatureResumed: !!resumed, signatureText: text || '' };
}

/**
 * What the retry draft of a send that failed remembers about the signature,
 * from what the compose window reported when it sent: it was put in (kept) or
 * deleted on purpose (removed), a reopened draft (draft), or never shown
 * (none). The text is what the block said when it was put in.
 */
export function signatureMemoryOfSend({ signature, signatureText } = {}) {
  return signatureMemory(signature === 'kept' || signature === 'removed', signature === 'draft', signatureText);
}

/**
 * The signature block in what the person is writing — never one inside the
 * quoted thread below it (an earlier email's, which is not theirs to keep or
 * lose and must not stop theirs being added).
 */
export function ownSignature(bodyEl) {
  if (!bodyEl) return null;
  const quote = bodyEl.querySelector('[data-compose-quote]');
  // Before the quote starts, not merely outside its wrapper: the quote is
  // spliced in as raw HTML, and stray closing tags in it close the wrapper
  // early, leaving the rest of the quoted email — an earlier signature
  // included — after it in the editor itself. Before the quote is also where
  // the server looks (aboveQuote), so the two can't disagree about it. A
  // signature inside the wrapper is after its start, so is excluded too.
  return [...bodyEl.querySelectorAll(`[${SIGNATURE_ATTR}]`)]
    .find((el) => !quote || !!(quote.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING)) || null;
}

/** A signature block's text, whitespace evened out — to tell whether it has been edited. */
export function signatureText(el) {
  return el ? String(el.textContent || '').replace(/\s+/g, ' ').trim() : '';
}

/**
 * A signature the person has typed into or changed is their own writing
 * now: its marker comes off, so nothing treats that text as "not written"
 * (the blank-email checks would otherwise ignore it) and the server never
 * adds another. `original` is signatureText() of the block as inserted.
 * Returns true when it let one go.
 */
export function releaseEditedSignature(bodyEl, original) {
  const el = ownSignature(bodyEl);
  if (!el || !original || signatureText(el) === original) return false;
  el.removeAttribute(SIGNATURE_ATTR);
  return true;
}

/** The part of a body above the quoted thread (all of it when there is none). */
export function aboveQuote(html) {
  const s = String(html || '');
  const at = s.search(/<[a-z][^>]*\bdata-compose-quote\b/i);
  return at < 0 ? s : s.slice(0, at);
}

/**
 * The To, Cc and Bcc fields ("a@x.test; Name <b@y.test>") as one list of
 * what was typed, blanks dropped. The server reads the addresses out.
 */
export function recipientList(...fields) {
  return fields
    .flatMap((f) => (Array.isArray(f) ? f : String(f || '').split(/[,;]/)))
    .map((r) => String(r || '').trim())
    .filter(Boolean);
}

/**
 * {enabled, html, text} for this compose, or null if it couldn't be had.
 * With `recipients` (recipientList) the server is told who the email is for
 * — in the request's body, never its address — and the answer says
 * `promotions: false` when it came without the banner.
 */
export async function fetchComposeSignature(kind, fetchImpl = (...a) => fetch(...a), recipients = null) {
  try {
    const res = recipients && recipients.length
      ? await fetchImpl('/api/mail/signature', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ compose: kind, recipients }),
      })
      : await fetchImpl(`/api/mail/signature?compose=${encodeURIComponent(kind)}`);
    if (!res || !res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * Make the signature in the window carry the promotion banner, or not, as
 * `html` (the signature for the people now on the email) does. Only the
 * person's own, still-marked signature: one they deleted stays deleted, and
 * one they typed into is theirs (releaseEditedSignature) and is left alone.
 * Taking the banner out removes just that element; putting it back swaps the
 * block for the fresh one. Returns true when it changed anything.
 */
export function matchPromotion(bodyEl, html) {
  const el = ownSignature(bodyEl);
  if (!el || !html) return false;
  const fresh = sanitizedFragment(html);
  const wanted = !!fresh.querySelector(`[${PROMOTION_ATTR}]`);
  const banners = [...el.querySelectorAll(`[${PROMOTION_ATTR}]`)];
  if (wanted === (banners.length > 0)) return false;
  if (!wanted) {
    banners.forEach((b) => b.remove());
    return true;
  }
  if (!fresh.querySelector(`[${SIGNATURE_ATTR}]`)) return false;
  el.replaceWith(fresh);
  return true;
}

/**
 * Put the signature into the compose body element: before the quoted
 * thread (data-compose-quote) or at the end. Returns false when there's
 * nothing to do (no HTML, or one is already there). Inserting a node never
 * moves the caret, so this is safe however late the fetch lands.
 */
export function insertSignatureBlock(bodyEl, html) {
  if (!bodyEl || !html || ownSignature(bodyEl)) return false;
  const doc = bodyEl.ownerDocument || document;
  const block = doc.createElement('div');
  block.setAttribute('style', 'margin-top:12px;');
  // The design is HTML an admin wrote in OnPoint, going into a live part
  // of this page: nothing in it that could run code gets in (lib/safeHtml.js).
  block.appendChild(sanitizedFragment(html));
  if (!block.firstChild) return false;
  const quote = bodyEl.querySelector('[data-compose-quote]');
  if (quote && quote.parentNode === bodyEl) {
    bodyEl.insertBefore(block, quote);
  } else {
    bodyEl.appendChild(block);
  }
  return true;
}

/**
 * What the server should do about the signature:
 *   kept    — it's in the body;
 *   removed — we showed it and the person deleted it: leave it out;
 *   draft   — a reopened Outlook draft: send it as it stands (Outlook can
 *             drop our marker from a signature it saved, so the server
 *             couldn't tell one was there — adding one could make two);
 *   none    — we never showed one: add it if they have one.
 */
export function signatureFlag(inserted, bodyHtml, { resumed = false } = {}) {
  // Only the person's own: a signature quoted from an earlier email says nothing about theirs.
  if (new RegExp(SIGNATURE_ATTR, 'i').test(aboveQuote(bodyHtml))) return 'kept';
  if (inserted) return 'removed';
  return resumed ? 'draft' : 'none';
}

/**
 * The body without the signature block — for "has the person written
 * anything?" checks, which the signature on its own mustn't trip.
 */
export function withoutSignature(html) {
  const s = String(html || '');
  if (!new RegExp(SIGNATURE_ATTR, 'i').test(s) || typeof DOMParser === 'undefined') return s;
  const doc = new DOMParser().parseFromString(`<div id="pp-root">${s}</div>`, 'text/html');
  const root = doc.getElementById('pp-root');
  root.querySelectorAll(`[${SIGNATURE_ATTR}]`).forEach((el) => {
    // insertSignatureBlock's wrapper goes too, if that's all it held.
    const wrapper = el.parentElement;
    el.remove();
    if (wrapper && wrapper !== root && !wrapper.textContent.trim() && !wrapper.querySelector('img')) wrapper.remove();
  });
  return root.innerHTML;
}
